import React, {useEffect, useState} from "react";
import {RemoveNoneNumberCharacters, valueIfNull} from "../../Hooks/UseGenericFunctions";
import {axiosInstance} from "../../Api/AxiosConfig";
import {XCircleIcon} from "@heroicons/react/24/solid";
import {CarzelleTooltip} from "../../Components/System/Messages/CarzelleTooltip";
import {ShortMessageModal} from "../../Components/System/Messages/ShortMessageModal";
import {useDisclosure} from "@mantine/hooks";
import {NumberInput} from "@mantine/core";
import {VATIcon} from "../../Components/Cars/VATIcon";

export const OfferInput = React.memo(({car, className, inputClass, mobileMode=false, showSubmitButton=false, idPrefix = "offerAmount", fnUpdate}) => {
    const [isAdjusted, setIsAdjusted] = useState(false);
    const [adjustedOfferAmount, setAdjustedOfferAmount] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [opened, {open, close}] = useDisclosure(false);

    const setDefaultValueOfOfferAmount = () => {
        setAdjustedOfferAmount('');
        if (car.offer_amount_unconfirmed !== null) {
            setAdjustedOfferAmount(car.offer_amount_unconfirmed);
        }

        if (car.offer_amount_unconfirmed === null && car.offer_amount_confirmed > 0) {
            setAdjustedOfferAmount(car.offer_amount_confirmed);
        }

        if (mobileMode) {
            setAdjustedOfferAmount(car.price);
        }
    }

    const addOffer = (hash, amount) => {
        axiosInstance
            .post("shop/add-to-basket", {carHash: hash, offerAmount: parseInt(amount, 10)})

            .then((response) => {
                if (response.data.error_message !== '') {
                    setDefaultValueOfOfferAmount();
                    setErrorMessage(response.data.error_message);
                    open();
                } else {
                    fnUpdate();
                }
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            });
    }

    useEffect(() => {
        setIsAdjusted(false);
        setDefaultValueOfOfferAmount();
    }, [car]); // eslint-disable-line react-hooks/exhaustive-deps

    const removeFromBasket = (hash) => {
        axiosInstance
            .delete("shop/remove-from-basket/" + hash)

            .then((response) => {
                console.log(response);
                fnUpdate();
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            });
    }

    // Remove the car from the basket when:
    // The unconfirmed offer is different from 0 AND the car is in the basket AND the input field is empty or 0
    // Add the car to the basket when:
    // The car doesn't have an confirmed offer AND the unconfirmed value is different from the input field AND the input field is different from 0
    // The car has a confirmed offer AND the confirmed value is different from the input field AND the unconfirmed value is different from the input field AND the input field is different from 0
    const addOrRemoveFromBasket = () => {
        let controlValueAdjustedOfferAmount =  document.getElementById(idPrefix + '_' + car.hash).value;
        controlValueAdjustedOfferAmount = RemoveNoneNumberCharacters(controlValueAdjustedOfferAmount);

        if (car.in_basket && valueIfNull(car.offer_amount_unconfirmed, 0) > 0 && (controlValueAdjustedOfferAmount === '' || controlValueAdjustedOfferAmount === '0')) {
            removeFromBasket(car.hash);
        } else {
            if (car.offer_amount_confirmed === null && car.offer_amount_unconfirmed !== Number(controlValueAdjustedOfferAmount) && Number(controlValueAdjustedOfferAmount) > 0) {
                addOffer(car.hash, controlValueAdjustedOfferAmount);
            }

            if (car.offer_amount_confirmed !== null && Number(car.offer_amount_unconfirmed) !== Number(controlValueAdjustedOfferAmount) && Number(controlValueAdjustedOfferAmount) > 0 && Number(car.offer_amount_confirmed) !== Number(controlValueAdjustedOfferAmount)) {
                addOffer(car.hash, controlValueAdjustedOfferAmount);
            }
        }
    };

    const showDeleteButton = () => {
        return car.offer_amount_unconfirmed !== null;
    }


    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            addOrRemoveFromBasket();
        }

        if (e.key === 'Escape' && isAdjusted && !showSubmitButton) {
            setDefaultValueOfOfferAmount();
        }
    };

    const borderColor = () => {
        let result = 'border-gray-200';

        if (car.offer_amount_confirmed > 0) {
            result = 'border-green-500';
        }

        if (car.offer_amount_unconfirmed !== null) {
            result = 'border-purchase-color';
        }

        return result;
    }

    const pillBackgroundColor = () => {
        let result = '';

        if (car.offer_amount_confirmed > 0) {
            result = 'bg-green-500';
        }

        if (car.offer_amount_unconfirmed > 0) {
            result = 'bg-purchase-color';
        }

        return result
    }

    const tooltip = () => {
        let result = "";
        if (car.offer_amount_unconfirmed > 0 && car.offer_amount_confirmed === null) result = "This offer is not confirmed. Please click the Basket button at the top of the page to confirm offer(s)";
        if (car.offer_amount_unconfirmed === null && car.offer_amount_confirmed === null) result = "Enter your offer on this car and it will be added to your shopping basket. We will inform you if the seller accepts your offer";
        if (car.offer_amount_unconfirmed === null && car.offer_amount_confirmed > 0) result = "This offer is confirmed. We will get back to you";
        return result;
    }

    const infoPillText = () => {
        let result = "";
        if (car.offer_amount_unconfirmed > 0 && car.offer_amount_confirmed === null) result = "Unsubmitted offer";
        if (car.offer_amount_confirmed > 0) result = "Submitted offer";
        if (car.offer_amount_unconfirmed > 0 && car.offer_amount_confirmed > 0) result = "Raised but unsubmitted offer";
        return result;
    }

    let infoText = infoPillText();

    const RightSection = (car) => {
        return (
            <div className="flex items-center">
                {car.currency}
                {showSubmitButton &&
                    <div className={`ml-4 cursor-pointer flex items-center`}>
                        <div className="rounded bg-purchase-color px-2 py-1 whitespace-nowrap text-white flex items-center justify-center" onClick={addOrRemoveFromBasket}>{mobileMode ? (parseInt(adjustedOfferAmount) === parseInt(car.price) ? 'Buy Now' : 'Make Offer') : 'Submit'}</div>
                    </div>
                }
            </div>
        )
    }

    return (
        <div className="relative">
            <CarzelleTooltip width={400} message={tooltip()}>
                <div>
                    <div className={`relative ${className}`}>
                        {mobileMode && <div className="absolute top-0 left-2 z-10 flex items-center h-full"><VATIcon vatStatusId={car.vat_status_id} /></div>}
                        <NumberInput
                            id={idPrefix + '_' + car.hash}
                            step={car.currency === "EUR" ? 100 : 1000}
                            value={adjustedOfferAmount}
                            allowDecimal={false}
                            decimalSeparator=","
                            valueIsNumericString={true}
                            placeholder="Offer"
                            rightSection={RightSection(car)}
                            rightSectionWidth={showSubmitButton ? 120 : 20}
                            thousandSeparator="."
                            className={`placeholder-left placeholder-right text-right ${borderColor()} rounded h-full`}
                            classNames={{
                                input: `h-full ${inputClass} ${mobileMode ? 'pl-10' : ''}`,
                                wrapper: 'h-full',
                            }}
                            onBlur={() => {if (!showSubmitButton) {addOrRemoveFromBasket()}}}
                            onKeyDown={handleKeyDown}
                            hideControls={true}
                            withKeyboardEvents={true}
                            onChange={(value) => {setAdjustedOfferAmount(value)}}
                            size="xs"
                            styles={{
                                section: {paddingRight: mobileMode ? '40px' : '20px'},
                            }}

                        />
                        <div className={`absolute z-10 text-xs left-0 w-full bottom-[-10px] px-2 py-[1px] text-white rounded-sm ${pillBackgroundColor()} bg-gray-500 cursor-pointer whitespace-nowrap ${infoText === '' ? 'hidden' : ''}`}>
                            {infoText}
                        </div>
                    </div>
                    <div className={`absolute z-50 top-[-4px] cursor-pointer right-0 ${showDeleteButton() ? '' : 'hidden'}`} onClick={() => {
                        document.getElementById(idPrefix + '_' + car.hash).value = '';
                        addOrRemoveFromBasket();
                    }}>
                        <XCircleIcon className="w-4 h-4 text-red-400"/>
                    </div>
                </div>
            </CarzelleTooltip>
            <ShortMessageModal message={errorMessage} opened={opened} close={close} onClose={() => {setErrorMessage('');fnUpdate()}}/>
        </div>
    )
});